import { createSignal, createRoot } from "solid-js";
import { localGet, localSet } from "@/shared/safeStorage";
import { request } from "@/api/http";

export type Language = "en" | "nb";

export interface LanguageOption {
  code: Language;
  label: string;
  flag: string;
}

export const languages: LanguageOption[] = [
  { code: "en", label: "English", flag: "GB" },
  { code: "nb", label: "Norsk", flag: "NO" },
];

// Translation data: module -> key -> translated text
type TranslationData = Record<string, string>;
type ModuleTranslations = Record<string, TranslationData>;

const STORAGE_KEY = "lobconfig_language";

function createI18n() {
  // Norwegian unless the visitor has chosen otherwise: the doors are made and sold in Norway, and
  // everyone who arrives without a stored preference is arriving for the first time.
  const saved = (localGet(STORAGE_KEY) as Language | null) || "nb";
  const [locale, setLocaleInternal] = createSignal<Language>(saved);
  const [translations, setTranslations] = createSignal<ModuleTranslations>({});
  const [loading, setLoading] = createSignal(false);

  // What has arrived, what is on its way, and what has ever been asked for. The third is what a language
  // switch reloads: the pages re-request their own modules when the locale changes, but the start page,
  // the consent dialog, the footer and the profile menu never ask twice — so without it a visitor who
  // switched language on the start page read raw keys until they reloaded.
  const loaded = new Set<string>();
  const inflight = new Map<string, Promise<void>>();
  const requested = new Set<string>();

  function loadModule(module: string): Promise<void> {
    requested.add(module);
    const lang = locale();
    const key = `${module}_${lang}`;
    if (loaded.has(key)) return Promise.resolve();
    // Two callers asking for the same module while it is on its way share the one request: on the
    // dashboard both the layout and the page ask for "dashboard" before either has an answer.
    const pending = inflight.get(key);
    if (pending) return pending;
    const run = fetchModule(module, lang, key).finally(() => inflight.delete(key));
    inflight.set(key, run);
    return run;
  }

  // Through the API client like every other call, for its retries on a cold start, the maintenance
  // reload and the per-attempt deadline — a fetch that hangs leaves the page in keys, and no answer
  // takes anywhere near this long. (The client reads locale() for its language header; both modules
  // only use each other inside functions, so the import cycle is harmless.)
  async function fetchModule(module: string, lang: Language, key: string): Promise<void> {
    setLoading(true);
    try {
      const data = await request<TranslationData>(`/translations/${module}/${lang}`, { timeoutMs: 15_000 });
      // The language moved on while this was in flight; the switch has already asked again.
      if (lang !== locale()) return;
      setTranslations((prev) => ({
        ...prev,
        [module]: data,
      }));
      loaded.add(key);
    } catch (e) {
      console.warn(`Failed to load translations for ${module}/${lang}`, e);
    } finally {
      setLoading(false);
    }
  }

  function setLocale(lang: Language) {
    // No-op if already on this language: clearing translations here without a locale CHANGE would leave the
    // reload effect (which watches locale()) un-triggered, so the strings would never reload → raw keys.
    if (lang === locale()) return;
    localSet(STORAGE_KEY, lang);
    loaded.clear();
    setTranslations({});
    setLocaleInternal(lang);
    for (const module of requested) void loadModule(module);
  }

  function t(key: string, fallback?: string): string {
    const allTranslations = translations();
    // Search across all loaded modules
    for (const module of Object.values(allTranslations)) {
      if (key in module) return module[key];
    }
    return fallback ?? key;
  }

  return { locale, setLocale, t, loadModule, loading, translations };
}

// Global singleton
const i18n = createRoot(createI18n);

export const { locale, setLocale, t, loadModule, loading: translationsLoading } = i18n;
